/**
 * Find Problems — the client side of `POST /modules/{id}/quality-check`.
 *
 * The actual check logic lives entirely server-side (`backend/quality_checks.py`
 * — geometry validity, overlap, connectivity, statistics); this file's only
 * job is the request/response shape, kept as a thin mirror of the backend's
 * Pydantic schemas rather than adding a second copy of any of that logic here.
 */

import { api } from './apiClient';

export interface QualityRelationshipRule {
  thisField: string;
  referenceLayerId: string;
  referenceField: string;
}

/** One layer's check selection and parameters for one run. Every field is
 *  optional on the wire — the server skips a check whose parameter was left
 *  empty rather than erroring. */
export interface QualityCheckSettings {
  checks: string[];
  duplicateMatchMode?: 'geometry' | 'geometry_and_attributes';
  requiredFields?: string[];
  minOverlapPercent?: number;
  snapToleranceMeters?: number;
  numericFields?: string[];
  categoricalFields?: string[];
  orphanReferenceLayerId?: string;
  orphanSnapToleranceMeters?: number;
  relationshipRules?: QualityRelationshipRule[];
  statusField?: string;
  allowedStatusValues?: string[];
  statusCheckConnectivity?: boolean;
}

export interface QualityProblem {
  id: string;
  layerId: string;
  layerName: string;
  check: string;
  severity: 'critical' | 'warning' | 'info';
  featureIndex: number | null;
  message: string;
  detail: Record<string, unknown>;
  geometry: GeoJSON.Geometry | null;
  /** The flagged feature's own attributes, for the export — not used for
   *  display in the panel itself. */
  properties: Record<string, unknown>;
}

export interface QualityCheckLayerSummary {
  layerId: string;
  layerName: string;
  totalFeatures: number;
  scannedFeatures: number;
  problemCount: number;
}

export interface QualityCheckResult {
  problems: QualityProblem[];
  summary: QualityCheckLayerSummary[];
  totalProblems: number;
}

export const qualityCheckService = {
  async run(
    moduleId: string,
    layers: { layerId: string; settings: QualityCheckSettings }[],
  ): Promise<QualityCheckResult> {
    return api.post<QualityCheckResult>(
      `/modules/${encodeURIComponent(moduleId)}/quality-check`,
      { layers },
    );
  },
};
